import React from 'react';
import { shape, string, func, bool } from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { I18n } from 'utils/i18n';

// Dynamic field options (company, employee, date fields)
const DYNAMIC_FIELDS = [
  { value: 'fullName', label: 'fullName', group: 'employee', type: 'text' },
  { value: 'firstName', label: 'firstName', group: 'employee', type: 'text' },
  { value: 'surName', label: 'surName', group: 'employee', type: 'text' },
  { value: 'companyName', label: 'companyName', group: 'company', type: 'text' },
  { value: 'email', label: 'email', group: 'employee', type: 'text' },
  { value: 'phone', label: 'phone', group: 'employee', type: 'text' },
  { value: 'division', label: 'division', group: 'employee', type: 'text' },
  { value: 'code', label: 'code', group: 'employee', type: 'text' },
  { value: 'jobTitle', label: 'jobTitle', group: 'employee', type: 'text' },
  { value: 'department', label: 'department', group: 'employee', type: 'text' },
  { value: 'location', label: 'location', group: 'employee', type: 'text' },
  { value: 'reportsTo', label: 'reportsTo', group: 'employee', type: 'text' },
  { value: 'currentDate', label: 'currentDate', group: 'date', type: 'date' },
];

const FieldConfigDialog = ({ open, onClose, field, onSave }) => {
  // Helper function to determine prefill type from value
  const getPrefillType = (prefillValue, fieldType) => {
    if (!prefillValue) {
      // Date fields default to dynamic (currentDate)
      return fieldType === 'date' ? 'dynamic' : 'none';
    }
    if (typeof prefillValue === 'object' && prefillValue.field) {
      return 'dynamic';
    }
    return 'static';
  };

  const [config, setConfig] = React.useState({
    name: '',
    mandatory: true,
    editable: true,
    isYesNo: false,
    ...field,
  });

  const [prefillType, setPrefillType] = React.useState(() =>
    getPrefillType(field.prefillValue, field.type),
  );
  const [prefillValue, setPrefillValue] = React.useState(() => {
    if (!field.prefillValue) {
      // Date fields default to currentDate
      return field.type === 'date' ? 'currentDate' : '';
    }
    if (typeof field.prefillValue === 'object' && field.prefillValue.field) {
      return field.prefillValue.field;
    }
    return field.prefillValue;
  });

  React.useEffect(() => {
    setConfig({
      name: '',
      mandatory: true,
      editable: true,
      isYesNo: false,
      ...field,
    });
    setPrefillType(getPrefillType(field.prefillValue, field.type));
    if (!field.prefillValue) {
      // Date fields default to currentDate
      setPrefillValue(field.type === 'date' ? 'currentDate' : '');
    } else if (typeof field.prefillValue === 'object' && field.prefillValue.field) {
      setPrefillValue(field.prefillValue.field);
    } else {
      setPrefillValue(field.prefillValue);
    }
  }, [field]);

  const handleChange = prop => event => {
    setConfig({ ...config, [prop]: event.target.value });
  };

  const handleSwitchChange = prop => event => {
    setConfig({ ...config, [prop]: event.target.checked });
  };

  const handlePrefillTypeChange = event => {
    const newType = event.target.value;
    setPrefillType(newType);
    // Reset prefill value when type changes
    if (newType === 'none') {
      setPrefillValue('');
    } else if (newType === 'static') {
      setPrefillValue(field.type === 'checkbox' ? true : '');
    } else if (newType === 'dynamic') {
      // Set default dynamic value based on field type
      if (field.type === 'date') {
        setPrefillValue('currentDate');
      } else {
        setPrefillValue('fullName');
      }
    }
  };

  const handlePrefillValueChange = event => {
    const value = event.target.value;

    // If name is empty and selecting a dynamic field, copy the field label to name
    if (prefillType === 'dynamic' && !config.name && value) {
      const selectedField = DYNAMIC_FIELDS.find(f => f.value === value);
      if (selectedField) {
        setConfig({
          ...config,
          name: I18n.t(`Fields.ConfigDialog.dynamicFields.${selectedField.label}`),
        });
      }
    }

    if (field.type === 'checkbox' && prefillType === 'static') {
      setPrefillValue(value === 'true');
    } else {
      setPrefillValue(value);
    }
  };

  const handleSave = () => {
    let finalPrefillValue;

    if (prefillType === 'none' || !prefillValue) {
      finalPrefillValue = undefined;
    } else if (prefillType === 'static') {
      finalPrefillValue = prefillValue;
    } else if (prefillType === 'dynamic') {
      // Dynamic field (company, employee, or date field)
      finalPrefillValue = { field: prefillValue };
    }

    const updatedField = { ...config };
    if (finalPrefillValue !== undefined) {
      updatedField.prefillValue = finalPrefillValue;
    } else {
      delete updatedField.prefillValue;
    }

    // Signature fields must always be mandatory
    if (field.type === 'signature') {
      updatedField.mandatory = true;
    }

    onSave({ ...field, ...updatedField });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {I18n.t('Fields.ConfigDialog.title')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={theme => ({
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
          })}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minWidth: 400,
        }}
      >
        <TextField
          size="small"
          label={I18n.t('Fields.ConfigDialog.name')}
          value={config.name}
          onChange={handleChange('name')}
          fullWidth
          sx={{ mt: 2 }}
        />

        {/* Prefill Value Configuration - not available for signature and checkbox fields */}
        {!['signature', 'checkbox'].includes(field.type) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{I18n.t('Fields.ConfigDialog.prefillLabel')}</InputLabel>
                <Select
                  value={prefillType}
                  onChange={handlePrefillTypeChange}
                  label={I18n.t('Fields.ConfigDialog.prefillLabel')}
                >
                  <MenuItem value="none">{I18n.t('Fields.ConfigDialog.prefillNone')}</MenuItem>
                  <MenuItem value="static">{I18n.t('Fields.ConfigDialog.prefillStatic')}</MenuItem>
                  {field.type !== 'checkbox' && (
                    <MenuItem value="dynamic">
                      {I18n.t('Fields.ConfigDialog.prefillDynamic')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {prefillType === 'static' && field.type !== 'checkbox' && (
                <TextField
                  size="small"
                  label={I18n.t('Fields.ConfigDialog.prefillValue')}
                  value={prefillValue}
                  onChange={handlePrefillValueChange}
                  sx={{
                    flex: 1,
                    // Fix calendar icon color in dark mode
                    ...(field.type === 'date' && {
                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        filter: theme => (theme.palette.mode === 'dark' ? 'invert(1)' : 'none'),
                      },
                    }),
                  }}
                  type={field.type === 'date' ? 'date' : 'text'}
                  slotProps={field.type === 'date' ? { inputLabel: { shrink: true } } : undefined}
                />
              )}

              {prefillType === 'static' && field.type === 'checkbox' && (
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>{I18n.t('Fields.ConfigDialog.prefillValue')}</InputLabel>
                  <Select
                    value={String(prefillValue)}
                    onChange={handlePrefillValueChange}
                    label={I18n.t('Fields.ConfigDialog.prefillValue')}
                  >
                    <MenuItem value="true">{I18n.t('Fields.ConfigDialog.checked')}</MenuItem>
                    <MenuItem value="false">{I18n.t('Fields.ConfigDialog.unchecked')}</MenuItem>
                  </Select>
                </FormControl>
              )}

              {prefillType === 'dynamic' && (
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>{I18n.t('Fields.ConfigDialog.dynamicField')}</InputLabel>
                  <Select
                    value={prefillValue}
                    onChange={handlePrefillValueChange}
                    label={I18n.t('Fields.ConfigDialog.dynamicField')}
                  >
                    {DYNAMIC_FIELDS.filter(f => {
                      // For date fields, only show date options
                      if (field.type === 'date') {
                        return f.type === 'date';
                      }
                      // For checkbox fields, only show boolean fields
                      if (field.type === 'checkbox') {
                        return f.type === 'boolean';
                      }
                      // For text/signature fields, show text fields (company and employee)
                      return f.type === 'text';
                    }).map(f => (
                      <MenuItem key={f.value} value={f.value}>
                        {I18n.t(`Fields.ConfigDialog.dynamicFields.${f.label}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        )}

        <FormControlLabel
          sx={theme => ({
            display: 'flex',
            marginLeft: theme.spacing(-1),
            marginRight: 0,
            marginTop: theme.spacing(-1),
          })}
          control={
            <Checkbox
              checked={config.mandatory}
              onChange={handleSwitchChange('mandatory')}
              disabled={field.type === 'signature'}
            />
          }
          label={I18n.t('Fields.ConfigDialog.mandatory')}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{I18n.t('Common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {I18n.t('Common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FieldConfigDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  field: shape({
    type: string.isRequired,
    name: string,
    mandatory: bool,
    editable: bool,
    isYesNo: bool,
  }).isRequired,
  onSave: func.isRequired,
};

export default FieldConfigDialog;
